import React from 'react';
import * as Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import withLoading from 'components/Loading/withLoading';
import { formatAxisAmmountValue } from 'helpers/chart';
import { RentData, rentGraphColors } from 'stores/workflowStores/finances/rent/model';
import i18n from 'i18next';
import { Currency } from 'enums';

type StackingOptions = undefined | 'normal' | 'percent';

interface Props {
  data: RentData;
  title?: string;
  className?: string;
  loading?: boolean;
  stacking?: StackingOptions;
  xCategories?: string[];
  currency?: Currency;
}

export interface ColumnData {
  name: string;
  data: number[];
  color?: string;
  stacking?: StackingOptions;
}

const ColumnGraph: React.FC<Props> = ({
  data,
  className,
  title,
  stacking,
  xCategories,
  currency,
}) => {
  const formatData = (): ColumnData[] => {
    if (!data || !data.chart) return [];
    const notStacked = ['requiredTotal'];
    const { chart } = data;

    return Object.keys(chart)
      .filter((key) => (chart as any)[key].value)
      .map((key) => ({
        name: i18n.t(`finQuestionnary_rent_graph_${key}`),
        stacking: notStacked.includes(key) ? undefined : 'normal',
        color: rentGraphColors[key].default,
        data: [(chart as any)[key].value],
      }));
  };

  const settings = {
    title: {
      text: title || '',
    },
    credits: {
      enabled: false,
    },
    legend: {
      align: 'right',
      layout: 'vertical',
      verticalAlign: 'middle',
      floating: false,
    },
    chart: {
      type: 'column',
    },
    yAxis: {
      min: 0,
      title: {
        text: '',
      },
      labels: {
        formatter: function (this: Highcharts.AxisLabelsFormatterContextObject<number>) {
          return formatAxisAmmountValue(this.value, currency);
        },
      },
    },
    tooltip: {
      headerFormat:
        typeof xCategories === 'undefined'
          ? ''
          : '<span style="font-size: 10px">{point.key}</span><br/>',
      pointFormat: `<span style="color:{point.color}">\u25CF</span> {series.name}: <b>{point.y} ${currency}</b><br/>`,
    },
    plotOptions: {
      series: {
        stacking: stacking,
        groupPadding: 0,
      },
    },
    xAxis: {
      categories: xCategories,
      labels: {
        enabled: false,
      },
    },
    series: formatData() as any,
    responsive: {
      rules: [
        {
          condition: {
            maxWidth: 400,
          },
          chartOptions: {
            legend: {
              align: 'center',
              layout: 'horizontal',
              verticalAlign: 'bottom',
              floating: false,
            },
          },
        },
      ],
    },
  };

  return (
    <HighchartsReact
      highcharts={Highcharts}
      options={settings as Highcharts.Options}
      className={className}
    />
  );
};

export default withLoading(ColumnGraph);
